import { useCallback, useEffect, useState } from "react";
import { toast } from "@/components/feedback/toast";
import { snapshotToConfig } from "@/lib/app-update/snapshot-to-config";
import { formatAdminDateTime } from "@/lib/format/admin-time";
import { adminFetch } from "@/api";

interface HistoryRow {
  id: string;
  platform: string;
  changedAt: string;
  changedBy: string;
  reason: string | null;
  snapshot: Record<string, unknown>;
}

export interface AppUpdateHistoryProps {
  platform: "android" | "ios";
}

// Moments: admins' clock (IST), labelled.
const fmt = formatAdminDateTime;

function summarize(snapshot: Record<string, unknown>): string {
  const min = typeof snapshot.minVersion === "string" ? snapshot.minVersion : "?";
  const latest = typeof snapshot.latestVersion === "string" ? snapshot.latestVersion : "?";
  const blocked = Array.isArray(snapshot.blockedVersions)
    ? snapshot.blockedVersions.length
    : 0;
  return `min ${min} · latest ${latest} · ${blocked} blocked`;
}

export function AppUpdateHistory({ platform }: AppUpdateHistoryProps) {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`/api/app-version/history?platform=${platform}`);
      if (!res.ok) return;
      const data = (await res.json()) as { history: HistoryRow[] };
      setRows(data.history);
    } finally {
      setLoading(false);
    }
  }, [platform]);

  useEffect(() => {
    load();
  }, [load]);

  const edit = (row: HistoryRow) => {
    const cfg = snapshotToConfig(platform, row.snapshot);
    window.dispatchEvent(
      new CustomEvent("fincurry:app-update:edit", {
        detail: { platform, config: cfg },
      })
    );
    toast.info(`Loaded snapshot ${fmt(row.changedAt)} into form`);
  };

  const rollback = async (id: string) => {
    if (
      !confirm(
        "Restore this snapshot as the live config? Current values will be saved to history first."
      )
    ) {
      return;
    }
    setRollingBack(id);
    try {
      const res = await adminFetch("/api/app-version/rollback", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ historyId: id }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        toast.error(data.error ?? "Rollback failed");
        return;
      }
      toast.success(`${platform} config restored`);
      window.location.reload();
    } finally {
      setRollingBack(null);
    }
  };

  return (
    <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <div className="text-[13px] font-semibold text-fc-text capitalize">
            {platform} · history
          </div>
          <div className="text-[11.5px] text-fc-text-dim">
            Snapshots taken before every save · latest {rows.length}
          </div>
          <div className="mt-1 text-[11px] text-fc-text-dim">
            Each row = state <span className="font-semibold">before</span> that
            save. Restore to rewind, Edit to fork.
          </div>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="py-6 text-center text-[12px] text-fc-text-dim">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-[12px] text-fc-text-dim">
          No history yet
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-fc-border">
          <table className="w-full min-w-[640px]">
            <thead className="bg-fc-overlay">
              <tr>
                {["When", "By", "Reason", "Summary", "Actions"].map(h => (
                  <th
                    key={h}
                    className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-fc-text-dim"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-fc-border">
                  <td className="px-3 py-2 font-mono text-[11.5px] text-fc-text">
                    {fmt(r.changedAt)}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-fc-text-secondary">
                    {r.changedBy}
                  </td>
                  <td
                    className="max-w-[260px] truncate px-3 py-2 text-[12px] text-fc-text-dim"
                    title={r.reason ?? undefined}
                  >
                    {r.reason ?? "—"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-fc-text-secondary">
                    {summarize(r.snapshot)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => edit(r)}
                        disabled={rollingBack !== null}
                        className="rounded-md border border-fc-border bg-fc-overlay px-2.5 py-1 text-[11.5px] font-semibold text-fc-text hover:brightness-110 disabled:opacity-60"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => rollback(r.id)}
                        disabled={rollingBack !== null}
                        className="rounded-md border border-fc-warn/40 bg-fc-warn/10 px-2.5 py-1 text-[11.5px] font-semibold text-fc-warn hover:brightness-110 disabled:opacity-60"
                      >
                        {rollingBack === r.id ? "Restoring…" : "Restore"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
